import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Circle, X, CheckCircle2, Loader2 } from 'lucide-react';
import { useSetupProgress, type SetupStepStatus } from '../../hooks/useSetupProgress';

// ============================================
// COMPONENTE DE ETAPA INDIVIDUAL
// ============================================

function StepItem({ step }: { step: SetupStepStatus }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!step.completed) {
      navigate(step.definition.navigateTo);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={step.completed || step.isLoading}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
        transition-colors duration-200
        ${step.completed
          ? 'bg-green-50 dark:bg-green-900/20 cursor-default'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
        }
        disabled:cursor-default
      `}
      aria-label={
        step.completed
          ? `${step.definition.label} — concluído`
          : `${step.definition.label} — pendente, clique para configurar`
      }
    >
      {/* Ícone */}
      <div className="flex-shrink-0">
        {step.isLoading ? (
          <Loader2 size={18} className="text-gray-400 animate-spin" />
        ) : step.completed ? (
          <Check size={18} className="text-green-600 dark:text-green-400" />
        ) : (
          <Circle size={18} className="text-gray-400 dark:text-gray-500" />
        )}
      </div>

      {/* Texto */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium ${
            step.completed
              ? 'text-green-700 dark:text-green-400 line-through'
              : 'text-gray-900 dark:text-gray-100'
          }`}
        >
          {step.definition.label}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {step.definition.description}
        </p>
      </div>
    </button>
  );
}

// ============================================
// ESTADO DE CELEBRAÇÃO (100%)
// ============================================

function CelebrationState() {
  return (
    <div className="flex flex-col items-center py-4 gap-2">
      <div className="celebration-check-container">
        <CheckCircle2
          size={48}
          className="text-green-500 dark:text-green-400 celebration-check"
        />
      </div>
      <p className="text-sm font-semibold text-green-700 dark:text-green-400">
        Configuração completa!
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Seu tenant está pronto para uso.
      </p>

      {/* CSS da animação inline para evitar dependência externa */}
      <style>{`
        .celebration-check-container {
          animation: celebrationBounce 0.6s ease-out;
        }
        .celebration-check {
          animation: celebrationPulse 1.5s ease-in-out infinite;
        }
        @keyframes celebrationBounce {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes celebrationPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function SetupProgress() {
  const {
    steps,
    percentage,
    isAllComplete,
    isLoading,
    isSetupDismissed,
    dismissSetup,
  } = useSetupProgress();

  // Não exibe se já foi dispensado
  if (isSetupDismissed) return null;

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
      role="region"
      aria-label="Progresso de configuração do tenant"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Configuração Inicial
        </h3>
        <button
          onClick={dismissSetup}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Dispensar progresso de configuração"
        >
          <X size={16} />
        </button>
      </div>

      {/* Barra de progresso */}
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Progresso
          </span>
          <span
            className={`text-xs font-semibold ${
              isAllComplete
                ? 'text-green-600 dark:text-green-400'
                : 'text-blue-600 dark:text-blue-400'
            }`}
          >
            {isLoading ? '...' : `${percentage}%`}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${
              isAllComplete
                ? 'bg-green-500'
                : 'bg-blue-500'
            }`}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      {/* Conteúdo: celebração ou lista de etapas */}
      <div className="px-4 pb-4">
        {isAllComplete ? (
          <CelebrationState />
        ) : (
          <div className="space-y-1">
            {steps.map((step) => (
              <StepItem key={step.definition.id} step={step} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
